import { useState } from 'react'

import { useRowKeys, type RowKeys } from '../stages/rowKeys'
import { appendItem, insertAt, moveItem, removeAt } from '../stages/stageLists'

/**
 * Die Bedienschritte einer bearbeitbaren Liste, an einer Stelle.
 *
 * Hinzufügen, entfernen, zurueckholen und verschieben laufen für die Vignetten
 * und für die Ersatzteile Schritt für Schritt gleich - nur die Felder eines
 * Eintrags unterscheiden sich. Die Operationen selbst kommen unverändert aus
 * ../stages/stageLists.ts, die stabilen React-Schlüssel aus ../stages/rowKeys.ts;
 * dieser Haken hält nur beides zusammen und merkt sich, was zuletzt entfernt
 * wurde.
 *
 * Gelöscht wird ohne Rückfrage, dafür lässt sich der letzte Eintrag mit einem
 * Griff zurueckholen - dieselbe Entscheidung wie bei den Etappeninhalten: eine
 * Rückfrage bei jedem Handgriff wäre auf einem Telefon am Strassenrand nur
 * lästig, ein versehentliches Antippen darf trotzdem nichts endgültig kosten.
 */
export interface ListEditing<T> {
  /** Schlüsselliste für die Zeilen, passend zu jedem Bedienschritt geführt. */
  keys: RowKeys
  /** Der zuletzt entfernte Eintrag, solange er zurückgeholt werden kann. */
  zurueck: T | null
  entfernen: (index: number) => void
  zurueckholen: () => void
  /** Hängt einen leeren Eintrag hinten an. */
  anhaengen: (leer: T) => void
  verschieben: (index: number, richtung: -1 | 1) => void
  /** Lässt den gemerkten Eintrag fallen, etwa beim Verlassen des Bearbeitens. */
  vergessen: () => void
}

export function useListEditing<T>(werte: T[], onChange: (next: T[]) => void): ListEditing<T> {
  const keys = useRowKeys(werte.length)
  const [zurueck, setZurueck] = useState<{ index: number; eintrag: T } | null>(null)

  return {
    keys,
    zurueck: zurueck?.eintrag ?? null,
    entfernen: (index) => {
      const eintrag = werte[index]
      if (eintrag === undefined) return

      keys.remove(index)
      setZurueck({ index, eintrag })
      onChange(removeAt(werte, index))
    },
    zurueckholen: () => {
      if (!zurueck) return

      keys.insert(zurueck.index)
      onChange(insertAt(werte, zurueck.index, zurueck.eintrag))
      setZurueck(null)
    },
    anhaengen: (leer) => {
      keys.append()
      // Nach dem Anhängen zeigt der gemerkte Index woandershin, also fallen
      // lassen. Dasselbe gilt beim Verschieben.
      setZurueck(null)
      onChange(appendItem(werte, leer))
    },
    verschieben: (index, richtung) => {
      keys.move(index, richtung)
      setZurueck(null)
      onChange(moveItem(werte, index, richtung))
    },
    vergessen: () => setZurueck(null),
  }
}
